import { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { Trophy, Crown, Award, Info, X } from "lucide-react";
import ProAvatarFrame, { ProName } from "@/components/ProAvatarFrame";
import ProfileModal from "@/components/ProfileModal";
import { useGeo } from "@/lib/geo";

const RANK_COLORS = ["#EAEB18", "#00F0FF", "#FF007F"];

export default function Leaderboard({ currentUserId }) {
  const { isIndia } = useGeo();
  const [period, setPeriod] = useState("daily");
  const [plan, setPlan] = useState("global"); // "global" | "darepass"
  const [entries, setEntries] = useState([]);
  const [myPosition, setMyPosition] = useState(null); // { rank, score, total, ... }
  const [prizes, setPrizes] = useState(null);
  const [showPrizeInfo, setShowPrizeInfo] = useState(false);
  // Profile-modal open-state — user_id of the player whose card is showing
  const [profileUserId, setProfileUserId] = useState(null);
  // Tracks the last-seen rank for each DarePass user, so we can flash a
  // rank-change effect only when they actually move up or down.
  const prevRanksRef = useRef({});
  // Map<user_id, "up" | "down"> — cleared 2.4s after the change.
  const [rankChanges, setRankChanges] = useState({});
  const timerRef = useRef(null);

  // Load prize config once (env-driven, changes rarely).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/leaderboard/prizes");
        if (!cancelled) setPrizes(res.data);
      } catch {}
    })();
    return () => { cancelled = true; };
  }, []);

  // When the tab (plan/period) changes, wipe the previous-rank memory so we
  // don't fire spurious "rank changed" pulses on the fresh dataset.
  useEffect(() => {
    prevRanksRef.current = {};
    setRankChanges({});
    setShowPrizeInfo(false);
  }, [period, plan]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      try {
        const [lbRes, myRes] = await Promise.all([
          api.get(`/leaderboard?period=${period}&plan=${plan}`),
          currentUserId
            ? api
                .get(`/leaderboard/my-position?period=${period}&plan=${plan}`)
                .catch(() => null)
            : Promise.resolve(null),
        ]);
        if (cancelled) return;
        const next = lbRes.data.entries || [];

        // Detect rank changes for DarePass users only.
        const prev = prevRanksRef.current;
        const nextPrev = {};
        const newChanges = {};
        next.forEach((e) => {
          nextPrev[e.user_id] = e.rank;
          if (!e.is_pro) return;
          const prior = prev[e.user_id];
          if (typeof prior === "number" && prior !== e.rank) {
            newChanges[e.user_id] = e.rank < prior ? "up" : "down";
          }
        });
        prevRanksRef.current = nextPrev;

        setEntries(next);
        setMyPosition(myRes?.data || null);
        if (Object.keys(newChanges).length > 0) {
          setRankChanges((r) => ({ ...r, ...newChanges }));
          // Clear each flag after the animation ends
          Object.keys(newChanges).forEach((uid) => {
            setTimeout(() => {
              setRankChanges((r) => {
                if (!(uid in r)) return r;
                const { [uid]: _drop, ...rest } = r;
                return rest;
              });
            }, 2400);
          });
        }
      } catch {
        // silent
      }
    };
    fetchData();
    timerRef.current = setInterval(fetchData, 3000);
    return () => {
      cancelled = true;
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [period, plan, currentUserId]);

  const currency = prizes?.currency === "INR" ? "₹" : (prizes?.currency || "₹");
  const currentPrizes = prizes?.[plan] || {};
  const first = currentPrizes["1st"];

  return (
    <div className="border border-[#00F0FF]/30 bg-[#0B0C10]">
      {/* Global / DarePass switch — top of leaderboard */}
      <div className="grid grid-cols-2 border-b border-[#00F0FF]/30" data-testid="leaderboard-plan-switch">
        <button
          onClick={() => setPlan("global")}
          data-testid="leaderboard-plan-global"
          className={`flex items-center justify-center gap-2 py-2.5 text-[10px] uppercase tracking-[0.25em] font-heading font-black transition-colors ${
            plan === "global"
              ? "bg-[#00F0FF]/15 text-[#00F0FF] border-b-2 border-[#00F0FF]"
              : "text-[#94A3B8] hover:text-[#00F0FF] border-b-2 border-transparent"
          }`}
        >
          <Trophy size={12} strokeWidth={2.5} /> Global
        </button>
        <button
          onClick={() => setPlan("darepass")}
          data-testid="leaderboard-plan-darepass"
          className={`flex items-center justify-center gap-2 py-2.5 text-[10px] uppercase tracking-[0.25em] font-heading font-black transition-colors ${
            plan === "darepass"
              ? "bg-[#EAEB18]/15 text-[#EAEB18] border-b-2 border-[#EAEB18]"
              : "text-[#94A3B8] hover:text-[#EAEB18] border-b-2 border-transparent"
          }`}
        >
          <Award size={12} strokeWidth={2.5} /> DarePass
        </button>
      </div>

      {/* Prize / ranking banner — India shows the cash-prize wording;
          the rest of the world sees a clean global-leaderboard header
          with zero INR / prize references. */}
      <div
        data-testid="prize-banner"
        className={`relative flex items-center justify-between gap-3 px-4 py-3 border-b ${
          plan === "darepass"
            ? "bg-gradient-to-r from-[#EAEB18]/20 via-[#EAEB18]/5 to-transparent border-[#EAEB18]/40"
            : "bg-gradient-to-r from-[#00F0FF]/15 via-[#00F0FF]/5 to-transparent border-[#00F0FF]/40"
        }`}
      >
        <div className="flex items-center gap-2">
          <Trophy size={18} strokeWidth={2.5} className={plan === "darepass" ? "text-[#EAEB18] glow-yellow" : "text-[#00F0FF]"} />
          <div className="font-heading font-black text-sm tracking-wider text-white">
            {isIndia
              ? (plan === "darepass" ? "DAREPASS WEEKLY PRIZE" : "DAILY CASH REWARDS")
              : (plan === "darepass" ? "DAREPASS EXCLUSIVE" : "GLOBAL LEADERBOARD")}
          </div>
          {isIndia && prizes && (
            <button
              type="button"
              onClick={() => setShowPrizeInfo((s) => !s)}
              data-testid="prize-info-btn"
              aria-label="Show prize amounts"
              className={`inline-flex items-center justify-center w-5 h-5 rounded-full border transition-colors ${
                plan === "darepass"
                  ? "border-[#EAEB18]/60 text-[#EAEB18] hover:bg-[#EAEB18]/15"
                  : "border-[#00F0FF]/60 text-[#00F0FF] hover:bg-[#00F0FF]/15"
              }`}
            >
              <Info size={12} strokeWidth={2.5} />
            </button>
          )}
        </div>
        <div className={`font-mono-arcade font-black text-[10px] uppercase tracking-widest ${plan === "darepass" ? "text-[#EAEB18]" : "text-[#00F0FF]"}`}>
          {isIndia ? "Top 3 Win" : "Live Ranking"}
        </div>

        {isIndia && prizes && showPrizeInfo && (
          <div
            data-testid="prize-info-popover"
            className={`absolute left-3 right-3 top-full mt-1 z-30 border bg-[#0B0C10] p-3 shadow-lg ${
              plan === "darepass" ? "border-[#EAEB18]/50" : "border-[#00F0FF]/50"
            }`}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-[0.25em] font-heading font-black text-white">
                Prize breakdown
              </div>
              <button
                type="button"
                onClick={() => setShowPrizeInfo(false)}
                data-testid="prize-info-close"
                aria-label="Close prize info"
                className="text-[#94A3B8] hover:text-white"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {["daily", "weekly"].map((p) => {
                const set = (prizes?.[plan] || {})[p] || {};
                const accent = plan === "darepass" ? "text-[#EAEB18]" : "text-[#00F0FF]";
                return (
                  <div key={p} data-testid={`prize-info-${p}`}>
                    <div className={`text-[9px] uppercase tracking-[0.25em] font-black mb-1 ${accent}`}>
                      {p}
                    </div>
                    <div className="space-y-0.5 font-mono-arcade text-[11px]">
                      {["1st", "2nd", "3rd"].map((r, i) => (
                        <div key={r} className="flex justify-between text-white">
                          <span style={{ color: RANK_COLORS[i] }}>#{i + 1}</span>
                          <span>
                            {currency}
                            {set[r] ?? "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-2 pt-2 border-t border-[#94A3B8]/15 text-[9px] uppercase tracking-widest text-[#94A3B8]">
              Amounts refresh daily · payouts via UPI
            </div>
          </div>
        )}
      </div>

      {/* Period switch */}
      <div className="flex items-center justify-end border-b border-[#00F0FF]/20 px-4 py-2">
        <div className="flex gap-1">
          {["daily", "weekly"].map((p) => (
            <button
              key={p}
              data-testid={`leaderboard-tab-${p}`}
              onClick={() => setPeriod(p)}
              className={`text-[10px] uppercase tracking-[0.2em] px-3 py-1 border transition-colors ${
                period === p
                  ? "border-[#EAEB18] text-[#EAEB18] bg-[#EAEB18]/10"
                  : "border-[#00F0FF]/30 text-[#94A3B8] hover:text-[#00F0FF]"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[520px] overflow-y-auto">
        {entries.length === 0 && (
          <div className="p-8 text-center text-xs uppercase tracking-[0.2em] text-[#94A3B8]" data-testid="leaderboard-empty">
            {plan === "darepass"
              ? "No DarePass scores yet. Be the first member on the board."
              : "No scores yet. Be the first."}
          </div>
        )}
        {entries.map((e) => {
          const isTop3 = e.rank <= 3;
          const isMe = e.user_id === currentUserId;
          const rankKey = e.rank === 1 ? "1st" : e.rank === 2 ? "2nd" : e.rank === 3 ? "3rd" : null;
          const prizeAmt = rankKey ? currentPrizes[rankKey] : null;
          const rankMove = e.is_pro ? rankChanges[e.user_id] : null; // "up" | "down" | undefined
          const proRowClass = e.is_pro
            ? `darepass-row ${rankMove === "up" ? "darepass-rank-up" : rankMove === "down" ? "darepass-rank-down" : ""}`
            : "";
          return (
            <div
              key={e.user_id}
              data-testid={`leaderboard-row-${e.rank}`}
              data-darepass={e.is_pro ? "1" : undefined}
              data-rank-move={rankMove || undefined}
              className={`flex items-center gap-3 px-4 py-2.5 border-b border-[#00F0FF]/10 transition-colors ${proRowClass} ${
                isMe ? "bg-[#EAEB18]/10 border-l-4 border-l-[#EAEB18]" : "hover:bg-[#00F0FF]/5"
              }`}
            >
              <div
                className="w-8 font-mono-arcade font-bold text-sm"
                style={{ color: isTop3 ? RANK_COLORS[e.rank - 1] : "#94A3B8" }}
              >
                #{String(e.rank).padStart(2, "0")}
              </div>
              {isTop3 && (
                <Crown size={14} strokeWidth={2.5} style={{ color: RANK_COLORS[e.rank - 1] }} />
              )}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <button
                  type="button"
                  data-testid={`leaderboard-avatar-btn-${e.rank}`}
                  onClick={() => setProfileUserId(e.user_id)}
                  aria-label={`View ${e.name}'s profile`}
                  className="focus:outline-none focus:ring-2 focus:ring-[#EAEB18] hover:opacity-80 transition-opacity"
                >
                  <ProAvatarFrame picture={e.picture} name={e.name} isPro={!!e.is_pro} size="sm" />
                </button>
                <button
                  type="button"
                  data-testid={`leaderboard-name-btn-${e.rank}`}
                  onClick={() => setProfileUserId(e.user_id)}
                  className="truncate text-sm text-white text-left hover:text-[#EAEB18] transition-colors cursor-pointer"
                >
                  <ProName name={e.name} isPro={!!e.is_pro} />
                  {isMe && (
                    <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-[#EAEB18]">you</span>
                  )}
                </button>
              </div>
              <div className="font-mono-arcade font-bold text-sm text-[#EAEB18] flex items-center gap-2">
                {isTop3 && (
                  <span
                    data-testid={`prize-badge-${e.rank}`}
                    className={`text-[9px] uppercase tracking-[0.15em] font-black px-1.5 py-0.5 ${
                      plan === "darepass" ? "bg-[#EAEB18] text-black" : "bg-[#00F0FF] text-black"
                    }`}
                  >
                    WIN
                  </span>
                )}
                {String(e.score).padStart(5, "0")}
              </div>
            </div>
          );
        })}

        {/* "You: #N" footer row — appears whenever the logged-in user has a
            score in the current period but sits below the visible top-100
            slice. Gives players outside the fold a clear signal of their
            actual standing (e.g. "#245") instead of an empty gap. */}
        {myPosition &&
          myPosition.rank &&
          !entries.some((e) => e.user_id === myPosition.user_id) && (
            <div
              data-testid="leaderboard-my-position"
              className="sticky bottom-0 flex items-center gap-3 px-4 py-3 border-t-2 border-[#EAEB18] bg-[#EAEB18]/10 backdrop-blur"
            >
              <div className="w-14 font-mono-arcade font-bold text-sm text-[#EAEB18]">
                #{myPosition.rank}
              </div>
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <button
                  type="button"
                  data-testid="leaderboard-my-position-avatar"
                  onClick={() => setProfileUserId(myPosition.user_id)}
                  className="focus:outline-none focus:ring-2 focus:ring-[#EAEB18]"
                >
                  <ProAvatarFrame
                    picture={myPosition.picture}
                    name={myPosition.user_name}
                    isPro={!!myPosition.is_pro}
                    size="sm"
                  />
                </button>
                <button
                  type="button"
                  onClick={() => setProfileUserId(myPosition.user_id)}
                  className="truncate text-sm text-white text-left hover:text-[#EAEB18] transition-colors"
                >
                  <ProName name={myPosition.user_name} isPro={!!myPosition.is_pro} />
                  <span className="ml-2 text-[9px] uppercase tracking-[0.2em] text-[#EAEB18] font-black">
                    you
                  </span>
                </button>
              </div>
              <div className="font-mono-arcade font-bold text-sm text-[#EAEB18]">
                {String(myPosition.score).padStart(5, "0")}
              </div>
            </div>
          )}
      </div>
      {profileUserId && (
        <ProfileModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
        />
      )}
    </div>
  );
}
